import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Clock, ArrowRight, Compass } from 'lucide-react';
import ScrollReveal from './ScrollReveal';
import { BHOPAL_GUIDE } from '../utils/cinematicData';

const BhopalGuide = () => {
  return (
    <section className="py-24 bg-gradient-to-b from-navy-900 to-navy-800 relative overflow-hidden">
      {/* Decorative Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D4AF37' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <ScrollReveal direction="up" className="text-center mb-16">
          <div className="flex items-center justify-center mb-6">
            <div className="h-px w-20 bg-gradient-to-r from-transparent to-gold-400" />
            <Compass className="mx-4 w-6 h-6 text-gold-400" />
            <div className="h-px w-20 bg-gradient-to-l from-transparent to-gold-400" />
          </div>
          
          <h2 className="text-5xl md:text-6xl font-serif text-white mb-6">
            {BHOPAL_GUIDE.title}
          </h2>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto font-light">
            {BHOPAL_GUIDE.description}
          </p>
        </ScrollReveal>

        {/* Landmarks Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto mb-16">
          {BHOPAL_GUIDE.landmarks.map((landmark, index) => (
            <ScrollReveal 
              key={index}
              direction="up"
              delay={index * 0.1}
            >
              <motion.div
                whileHover={{ y: -10 }}
                className="group bg-white/5 backdrop-blur-sm border border-gold-400/20 rounded-lg overflow-hidden hover:border-gold-400/60 transition-all duration-500"
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={landmark.image}
                    alt={landmark.name}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-900/90 to-transparent" />
                  
                  {/* Distance Badge */}
                  <div className="absolute bottom-4 left-4 flex items-center space-x-2 bg-gold-400/90 text-navy-900 px-3 py-1 rounded-full text-sm font-semibold">
                    <Clock className="w-3 h-3" />
                    <span>{landmark.distance}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-serif text-white mb-2 group-hover:text-gold-400 transition-colors duration-300">
                    {landmark.name}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {landmark.description}
                  </p>
                </div>
              </motion.div>
            </ScrollReveal>
          ))}
        </div>

        {/* Activities Section */}
        <ScrollReveal direction="up" delay={0.3}>
          <div className="bg-gradient-to-r from-gold-400/10 to-transparent border border-gold-400/20 rounded-lg p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-serif text-gold-400 mb-6 text-center">
              Curated Experiences
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {BHOPAL_GUIDE.activities.map((activity, index) => (
                <motion.div
                  key={index}
                  whileHover={{ x: 5 }}
                  className="flex items-center space-x-3 text-gray-300 hover:text-gold-400 transition-colors duration-300"
                >
                  <div className="w-2 h-2 bg-gold-400 rotate-45 flex-shrink-0" />
                  <span>{activity}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </ScrollReveal>

        {/* CTA */}
        <ScrollReveal direction="up" delay={0.4} className="text-center mt-12">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="group inline-flex items-center space-x-2 px-8 py-4 bg-gold-400 text-navy-900 font-semibold tracking-wider hover:bg-white transition-colors duration-300"
          >
            <span>PLAN YOUR BHOPAL JOURNEY</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
          </motion.button>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default BhopalGuide;
